import React from 'react'
import moment from 'moment'
import HighStock from 'highcharts/es-modules/masters/highstock.src.js'
import 'highcharts/es-modules/masters/modules/vector.src.js'
import 'highcharts/es-modules/masters/modules/exporting.src.js'
import 'highcharts/es-modules/masters/modules/accessibility.src.js'
import HighchartsReact from 'highcharts-react-official'
import Spinner from '../spinner/spinner'
import Predictions from './predictions'
import {ThemeSetting} from './theme'
import {chartOptions} from './config'
import {dateToString} from '../../utils/date'
import {prettyAmount} from '../../utils/amount'
import {close, fields, future, ghost, vector} from './fields'
import {chartPredictionSeries, finiteChartNumber} from './chartData'
import useApi, {CONTROLLER} from '../../hooks/useApi'
import Menu from './menu'
import './fix'

HighStock.setOptions(ThemeSetting);

const vectorEnabled = process.env.REACT_APP_CHART_VECTOR_ENABLED;
const futureEnabled = process.env.REACT_APP_CHART_FUTURE_ENABLED;

const Chart = ({
  ticker = "DJI", showPredictions = false, chartData,
  deepIndex: suppliedDeepIndex, loadData = true, loading: suppliedLoading = false,
}) => {
  const chart = React.useRef();
  const [isFuture, setFuture] = React.useState(false);
  const [isVector, setVector] = React.useState(false);
  const [value, setValue] = React.useState('daily_prediction');
  const apiOptions = React.useMemo((() => ({urlParams: {ticker}})), [ticker])
  const {
    loading: loadingFuture,
    payload: payloadFuture,
    execute: executeFuture
  } = useApi(CONTROLLER.CHART_FUTURE, apiOptions);
  const {
    loading: loadingVector,
    payload: payloadVector,
    execute: executeVector
  } = useApi(CONTROLLER.CHART_VECTOR, apiOptions);
  const {loading: loadingChart, payload: payloadChart, execute: executeChart} = useApi(CONTROLLER.CHART_FULL, apiOptions);
  const data = loadData ? payloadChart?.data : chartData;

  const [baseCloseDate, baseClose] = React.useMemo(() => {
    const last = data?.close?.[data?.close?.length - 1];
    if (!last || last.length < 2) {
      return [null, null];
    }
    return [last[0], last[1]];
  }, [data?.close]);

  const description = React.useMemo(() => {
    switch (data?.info?.ticker) {
      case "GSPC":
        return "S&P 500";
      case "DJI":
        return "Dow Industrial Average";
      case "IXIC":
        return "Nasdaq";
      default:
        return data?.info?.ticker;
    }
  }, [data?.info?.ticker]);

  const [minDate, maxDate] = React.useMemo(() => {
    const closeSeries = Array.isArray(data?.close) ? data.close : [];
    const lastClose = closeSeries[closeSeries.length - 1];
    const futureDate = data?.info?.predictions?.three_month_prediction_date;
    if (!lastClose || !futureDate) {
      return [null, null];
    }
    return [
      moment
        .utc(lastClose[0])
        .add(-1, 'month')
        .unix() * 1000,
      moment
        .utc(futureDate)
        .unix() * 1000,
    ]
  }, [data?.close, data?.info?.predictions]);

  const clickButton = React.useCallback(() =>
    chart.current?.chart?.rangeSelector?.clickButton(2), []);

  const clickRange = React.useCallback(() => {
    if (minDate != null && maxDate != null) {
      chart.current?.chart?.xAxis[0]?.setExtremes(minDate, maxDate);
    }
  }, [maxDate, minDate]);

  const futureToggle = React.useCallback(() => setFuture((value) => value
    ? (setTimeout(clickButton, 100) && false) : (setTimeout(clickRange, 100) && true)), [clickButton, clickRange]);

  const options = React.useMemo(() => ({
    ...chartOptions,
    exporting: {
      enabled: showPredictions,
      buttons: {
        ...(futureEnabled === "true" ? {
          futureButton: {
            className: isFuture ? 'highcharts-future-button highcharts-future-selected' : 'highcharts-future-button',
            text: 'Show Future Predictions',
            x: -200,
            y: 53,
            onclick: futureToggle,
          }
        } : {}),
        ...(vectorEnabled === "true" ? {
          vectorButton: {
            className: isVector ? 'highcharts-future-button highcharts-future-selected' : 'highcharts-future-button',
            text: 'Show Return Data',
            x: (showPredictions && value !== 'all') ? -350 : -10000,
            y: (showPredictions && value !== 'all') ? 53 : -10000,
            onclick: () => setVector(vector => !vector),
          },
        }: {}),
      },
    },
    title: {text: data?.info?.name},
    subtitle: {text: (value === 'all' ? 'All' : fields[value].title) + ' Prediction vs Close'},
    legend: {enabled: true},
    series: [
      {
        ...close,
        data: Array.isArray(data?.close) ? data.close : [],
        visible: true,
        showInLegend: true,
      },
      ...Object
        .keys(fields)
        .filter(field => chartPredictionSeries(data, field))
        .map(field => ({
            ...fields[field],
            data: chartPredictionSeries(data, field),
            visible: (value === field || value === 'all'),
            showInLegend: (value === field || value === 'all'),
          })
        ),
      {
        ...future,
        data: (Array.isArray(data?.close) && data.close.length
          ? [data.close[data.close.length - 1]]
          : [])
          .concat(payloadFuture?.data?.map(([x, y, title]) => ({x, y, title})) || []),
        visible: isFuture,
        showInLegend: isFuture,
        tooltip: {
          pointFormatter: function () {
            return this.title ? `${(this.title.replace('_', ' ') + ' PRED').toUpperCase()}: <b>${this.y}<b>` : false;
          },
        },
      },
      {
        ...vector,
        data: (value !== 'all' && payloadVector?.data)
          ? payloadVector?.data[value]?.map(({Date, Direction, Length, IsMatch}) => ({
            x: Date,
            y: 1,
            direction: Direction,
            length: 10 + Length,
            color: IsMatch ? close.color : fields[value].color,
          }))
          : null,
        visible: isVector && showPredictions && value !== 'all',
        tooltip: {
          pointFormat: false,
        },
      },
      {
        ...ghost,
        data: (value !== 'all' && payloadVector?.data)
          ? payloadVector?.data[value]?.map(({Date, IsMatch, PercentReturn}) => ({
            x: Date,
            y: 1,
            color: IsMatch ? close.color : fields[value].color,
            isMatch: IsMatch,
            percentReturn: PercentReturn,
          }))
          : null,
        visible: isVector && showPredictions && value !== 'all',
        tooltip: {
          pointFormatter: function () {
            return `<span style="color:${this.color}">Return</span>: <b>${this.percentReturn}%</b><br /><span style="color:${this.color}">Direction Match</span>: <b>${this.isMatch ? 'Yes' : 'No'}</b>`
          },
        },
      },
    ]
  }), [isVector, data, isFuture, futureToggle, showPredictions, value, payloadFuture, payloadVector]);

  const deep_index = React.useMemo(() => {
    const supplied = finiteChartNumber(suppliedDeepIndex);
    if (supplied !== undefined) {
      return supplied;
    }
    return finiteChartNumber(data?.info?.deep_index);
  }, [data?.info?.deep_index, suppliedDeepIndex]);

  React.useEffect(() => {
    if (loadData) {
      executeChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, loadData]);

  React.useEffect(() => {
    if (showPredictions) {
      vectorEnabled === "true" && executeVector();
      futureEnabled === "true" && executeFuture();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, showPredictions])

  React.useEffect(() => {
    setFuture(false);
    setTimeout(clickButton, 100);
  }, [ticker, showPredictions, value, clickButton]);

  if (suppliedLoading || (loadData && loadingChart) || loadingVector || loadingFuture) {
    return <Spinner/>
  }

  return (
    <>
      {!showPredictions && (
        <>
          <h2>
            Deep Index
            <span>
              {deep_index === undefined ? 'Unavailable' : prettyAmount(Math.round(deep_index), 0)}
            </span>
          </h2>
          <h6>
            Predicted close value of {data?.info?.name} for {dateToString(data?.info?.date, "MMMM Do, YYYY")}
          </h6>
        </>
      )}
      {showPredictions && (
        <>
          <h2>
            {description} prediction
            <span>
              {deep_index === undefined ? 'Unavailable' : prettyAmount(deep_index)}
            </span>
          </h2>
          <h6>
            Predicted close value of {data?.info?.name} stock price
            for {dateToString(data?.info?.date, "MMMM Do, YYYY")}
          </h6>
          <Predictions
            predictions={data?.info?.predictions}
            baseClose={baseClose}
            baseCloseDate={baseCloseDate}
          />
          <Menu
            predictions={data}
            value={value}
            onChange={setValue}
          />
        </>
      )}
      <div className="homeGraph">
        <HighchartsReact
          ref={chart}
          highcharts={HighStock}
          constructorType={"stockChart"}
          options={options}
        />
      </div>
    </>
  );
}

export default Chart;
